import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { useAppTheme } from "@hooks/useAppTheme";
import Box from "@memorang/ui/src/components/Box";
import { useMemo } from "react";
import { Text, TouchableOpacity } from "react-native";
import type { FilterChipsProps } from "./types";

const CHIP_HEIGHT = 40;
const CHIP_WIDTH = 121;
const BORDER_RADIUS = 20;
const DIVIDER_WIDTH = 1;

const FilterChips = ({
	chips,
	selectedChipId,
	handleSelectChip,
}: FilterChipsProps) => {
	const theme = useAppTheme();

	const getChipStyle = useMemo(
		() => (index: number, isSelected: boolean, totalChips: number) => {
			const isFirst = index === 0;
			const isLast = index === totalChips - 1;

			const borderRadiusStyle = {
				...(isFirst && {
					borderTopLeftRadius: BORDER_RADIUS,
					borderBottomLeftRadius: BORDER_RADIUS,
				}),
				...(isLast && {
					borderTopRightRadius: BORDER_RADIUS,
					borderBottomRightRadius: BORDER_RADIUS,
				}),
			};

			const borderStyle = {
				borderTopWidth: 1,
				borderBottomWidth: 1,
				borderLeftWidth: isFirst ? 1 : 0,
				borderRightWidth: isLast ? 1 : 0,
			};

			return {
				height: CHIP_HEIGHT,
				width: CHIP_WIDTH,
				minWidth: CHIP_WIDTH + 12,
				borderRadius: 0,
				gap: 8,
				paddingHorizontal: 12,
				paddingVertical: 10,
				justifyContent: "center" as const,
				alignItems: "center" as const,
				flexDirection: "row" as const,
				...borderRadiusStyle,
				...borderStyle,
				backgroundColor: isSelected
					? theme.colors.primaryContainer
					: theme.dark
						? theme.colors.inverseSurface
						: theme.colors.white,
				borderColor: theme.colors.outline,
			};
		},
		[
			theme.colors.primaryContainer,
			theme.colors.inverseSurface,
			theme.colors.white,
			theme.colors.outline,
			theme.dark,
		],
	);

	const getTextColor = (isSelected: boolean) => {
		if (isSelected) {
			return theme.colors.onPrimaryContainer;
		}
		return theme.dark ? theme.colors.inverseOnSurface : theme.colors.onSurface;
	};

	return (
		<Box flexDirection="row" maxWidth="100%" justifyContent="center">
			{chips.map((chip, index) => {
				const isSelected = selectedChipId === chip.id;

				return (
					<Box key={chip.id} flexDirection="row">
						<TouchableOpacity
							style={getChipStyle(index, isSelected, chips.length)}
							onPress={() => handleSelectChip(chip.id)}
							accessible
							accessibilityRole="button"
							accessibilityState={{ selected: isSelected }}
							accessibilityLabel={chip.label}
						>
							{isSelected && (
								<Icon
									name="check"
									size={16}
									color={theme.colors.onPrimaryContainer}
									style={{ marginRight: 4 }}
								/>
							)}
							<Text
								style={{
									fontSize: 14,
									fontWeight: "500",
									color: getTextColor(isSelected),
									textAlign: "center",
								}}
								numberOfLines={2}
							>
								{chip.label}
							</Text>
						</TouchableOpacity>

						{index < chips.length - 1 && (
							<Box
								style={{
									width: DIVIDER_WIDTH,
									height: CHIP_HEIGHT,
									backgroundColor: theme.colors.outline,
								}}
							/>
						)}
					</Box>
				);
			})}
		</Box>
	);
};

export default FilterChips;
export type { FilterChipsProps, FilterChipItem } from "./types";
